import { useTranslation } from "react-i18next";
import { providerLabel } from "./bedrockModelsDraft";
import styles from "./BedrockModels.module.css";

/** One provider on one route: the switch, how many exceptions sit under it, and whether any model is listed for it. */
export interface ProviderAllowItem {
  provider: string;
  route: string;
  routeLabel: string;
  on: boolean;
  exceptions: number;
  hasModels: boolean;
}

/**
 * The provider switches of the Bedrock Model Management page (AIA-109 provider allow): "Allow all Anthropic models (EU)" and so on, one per
 * provider and route. The two help paragraphs are always visible, not behind a disclosure, because what a provider allow and an exception
 * do beyond this group is exactly what an administrator must know before using them.
 */
export function ProviderAllows({ items, onToggle }: { items: ProviderAllowItem[]; onToggle: (item: ProviderAllowItem, on: boolean) => void }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section className={styles.section} aria-labelledby="bedrock-provider-allows-heading">
      <div className={styles.sectionHeader}>
        <h3 id="bedrock-provider-allows-heading" className={styles.sectionTitle}>
          {t("bedrockModels.providerAllows.titleCount", { count: items.length })}
        </h3>
      </div>
      <div className={styles.sectionIntro}>
        <p>{t("bedrockModels.providerAllows.later")}</p>
        <p>{t("bedrockModels.providerAllows.acrossGroups")}</p>
      </div>
      {items.length === 0 ? (
        <p className={styles.empty}>{t("bedrockModels.providerAllows.none")}</p>
      ) : (
        <ul className={styles.rows}>
          {items.map((item) => {
            const name = t("bedrockModels.providerAllows.toggle", { provider: providerLabel(item.provider), route: item.routeLabel });
            return (
              <li key={`${item.route}:${item.provider}`} className={styles.row}>
                <label className={styles.rowLabel}>
                  <input
                    type="checkbox"
                    role="switch"
                    className={styles.switch}
                    checked={item.on}
                    aria-label={name}
                    onChange={(event) => onToggle(item, event.target.checked)}
                  />
                  <span className={styles.rowText}>
                    <span>{name}</span>
                  </span>
                </label>
                <span className={styles.rowTags}>
                  {item.on && item.exceptions > 0 && <span className={styles.viaTag}>{t("bedrockModels.providerAllows.exceptions", { count: item.exceptions })}</span>}
                  {!item.hasModels && <span className={styles.unavailableTag}>{t("bedrockModels.providerAllows.noModels")}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
